/**
 * The start view's detection card (Option C, C3, plan §3.2 "Paste detection"): the filter holds text that parses as a
 * connection string, so the engine list gives way to what the string will fill — engine, fields (secrets masked), what
 * is left out and what is still needed — and **Continue with <engine>**. Nothing reaches the form before Continue (a
 * click, or Enter in the filter; there is no auto-continue, D16).
 *
 * A string several engines read alike (ruling after C3 review r5) shows "Reads as: …" instead of guessing: until one is
 * picked the card says so and Continue is unavailable; a pick previews that engine's reading.
 */
import { ClipboardPaste } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReasonButton } from '@/components/ui/ReasonButton';
import type { ConnectionStringFormat } from '../../services/connection-string/parseConnectionString';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { ConnectionEngineIcon } from '../../components/ConnectionEngineIcon';
import { PasteParseStatus } from './PasteParseStatus';
import { PasteReadsAs } from './PasteReadsAs';
import { choiceHeadline, formatLabel, pasteChoiceMessage, type PastePreview } from './pastePreview';

export type EngineChoice = { engines: DatabaseEngine[]; picked: DatabaseEngine | null; onPick: (engine: DatabaseEngine) => void };

type Props = {
  /** What the string (or the picked engine's reading of it) fills; null while an engine is still to be picked. */
  preview: PastePreview | null;
  format: ConnectionStringFormat;
  choice?: EngineChoice;
  onContinue?: () => void;
};

export const DETECTION_TITLE_ID = 'connection-string-detection-title';
export const CHOOSE_ENGINE_FIRST = 'Choose the engine it is for first';

export function PasteDetectionCard({ preview, format, choice, onContinue }: Props) {
  const formatText = formatLabel(preview?.format ?? format);
  return (
    <section
      aria-labelledby={DETECTION_TITLE_ID}
      className="rounded-lg border border-primary/40 bg-primary/5 p-4"
      data-testid="connection-string-detection"
      data-engine={preview?.engine}
      data-outcome={preview?.outcome ?? 'choice'}
    >
      <div className="flex items-start gap-3">
        {preview ? (
          // The engine's icon is the flip partner of its rail item's icon: on Continue it flies into the rail (the final UX
          // review's answer to C3's open question); the card's text fades with the start view.
          <span data-flip-key={`engine:${preview.engine}`} data-flip-layer="icon" className="flex shrink-0">
            <ConnectionEngineIcon engine={preview.engine} iconOnly size="lg" className="h-7 w-7" />
          </span>
        ) : (
          <ClipboardPaste className="h-7 w-7 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <h3 id={DETECTION_TITLE_ID} className="text-sm font-medium leading-tight">
            {preview ? preview.headline : choiceHeadline(format)}
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <ClipboardPaste className="h-3.5 w-3.5" aria-hidden />
            {formatText ? `${formatText} format. ` : ''}Nothing is filled in until you continue.
          </p>
          {choice && (
            <div className="mt-2">
              <PasteReadsAs engines={choice.engines} picked={choice.picked} onPick={choice.onPick} />
            </div>
          )}
          <div className="mt-2.5">
            {preview ? (
              <PasteParseStatus preview={preview} failed={false} />
            ) : (
              choice && (
                <p className="text-xs text-muted-foreground" data-testid="connection-string-choose-engine">
                  {pasteChoiceMessage(choice.engines)}
                </p>
              )
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {preview && onContinue ? (
          <Button type="button" size="sm" onClick={onContinue} data-testid="connection-string-continue-button">
            Continue with {preview.engineLabel}
          </Button>
        ) : (
          <ReasonButton type="button" size="sm" reason={CHOOSE_ENGINE_FIRST} data-testid="connection-string-continue-button">
            Continue
          </ReasonButton>
        )}
      </div>
    </section>
  );
}
